// Hero Section Editor Component
import { useRef, useState } from 'react';
import { Loader2, Plus, X } from 'lucide-react';
import { cssVars, getTagColor } from './editorConstants';
import { AppButton } from '../apps';

export default function HeroSection({ 
  hero, 
  connect, 
  onUpdateHero, 
  onPhotoUpload, 
  onEditSocials,
  uploadingPhoto,
  apps = [],
  slug = '',
  onEditPitchApp,
  onOpenAppsPanel,
  onEditApp
}) {
  const photoInputRef = useRef(null);
  
  console.log('[HeroSection] Rendering hero section for:', hero?.name);

  const nameParts = (hero?.name || 'Your Name').split(' ');
  const firstName = nameParts[0];
  const lastName = nameParts.slice(1).join(' ');
  
  // Find pitch app (always show it)
  const pitchApp = apps.find(a => a.type === 'pitch');
  const isPitchEnabled = pitchApp?.enabled;

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      console.log('[HeroSection] Photo selected:', file.name);
      onPhotoUpload(file);
    }
    // Reset input
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  return (
    <div style={{ 
      background: cssVars.bgWhite,
      borderRadius: '24px',
      padding: 'clamp(30px, 5vw, 50px)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      gap: 'clamp(30px, 5vw, 60px)',
      alignItems: 'center',
      marginBottom: '24px'
    }}>
      <div>
        <h1 
          className="font-heading editable-field"
          style={{ 
            fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
            fontWeight: '400',
            color: cssVars.text,
            lineHeight: '1.1',
            letterSpacing: '-0.03em',
            marginBottom: 'clamp(6px, 1vh, 10px)'
          }}
          contentEditable
          suppressContentEditableWarning
          onBlur={(e) => onUpdateHero('name', e.target.textContent)}
        >
          <span style={{ fontStyle: 'italic' }}>{firstName}</span>{lastName ? ` ${lastName}` : ''}
        </h1>
        
        <p 
          className="editable-field"
          style={{ fontSize: 'clamp(1rem, 1.5vw, 1.15rem)', color: cssVars.textSecondary, marginBottom: 'clamp(8px, 1.5vh, 14px)', fontWeight: '600' }}
          contentEditable
          suppressContentEditableWarning
          onBlur={(e) => onUpdateHero('role', e.target.textContent)}
        >
          {hero?.role || 'Your Role at Company'}
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: 'clamp(16px, 2.5vh, 24px)', fontSize: '0.9rem' }}>
          {/* Location */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: cssVars.textSecondary }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cssVars.textMuted} strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
            </svg>
            <span className="editable-field" contentEditable suppressContentEditableWarning onBlur={(e) => onUpdateHero('location', e.target.textContent)}>
              {hero?.location || 'City'}
            </span>
          </div>
          
          {/* Email */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: cssVars.textSecondary }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cssVars.textMuted} strokeWidth="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
            </svg>
            <span className="editable-field" contentEditable suppressContentEditableWarning onBlur={(e) => onUpdateHero('email', e.target.textContent)}>
              {hero?.email || 'email@example.com'}
            </span>
          </div>
          
          {/* Website */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: cssVars.textSecondary }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cssVars.textMuted} strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
            </svg>
            <span 
              className="editable-field" 
              contentEditable 
              suppressContentEditableWarning 
              onBlur={(e) => onUpdateHero('website', e.target.textContent)}
              style={{ cursor: 'text' }}
            >
              {hero?.website || (hero?.email?.includes('@') ? hero.email.split('@')[1] : 'website.com')}
            </span>
          </div>
        </div>

        <p 
          className="editable-field"
          style={{ fontSize: 'clamp(0.95rem, 1.3vw, 1.05rem)', color: cssVars.textSecondary, lineHeight: '1.7', maxWidth: '480px', fontWeight: '300' }}
          contentEditable
          suppressContentEditableWarning
          onBlur={(e) => onUpdateHero('subtitle', e.target.textContent)}
        >
          {hero?.subtitle || 'A short bio about yourself...'}
        </p>

        {/* Badge */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          background: cssVars.accentLight,
          color: cssVars.accent,
          padding: '0.4rem 1rem',
          borderRadius: '100px',
          fontSize: '0.8rem',
          fontWeight: '500',
          marginTop: 'clamp(12px, 2vh, 18px)'
        }}>
          <span>✦</span>
          <span className="editable-field" contentEditable suppressContentEditableWarning onBlur={(e) => onUpdateHero('badge', e.target.textContent)}>
            {hero?.badge || 'your focus area'}
          </span>
        </div>

        {/* Tags Section */}
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '8px', 
          marginTop: '12px',
          alignItems: 'center'
        }}>
          {(hero?.tags || []).map((tag, i) => {
            const tagColor = getTagColor(tag);
            return (
              <div
                key={i}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: tagColor.bg,
                  color: tagColor.text,
                  padding: '0.35rem 0.85rem',
                  borderRadius: '100px',
                  fontSize: '0.75rem',
                  fontWeight: '500'
                }}
              >
                <span 
                  className="editable-field" 
                  contentEditable 
                  suppressContentEditableWarning 
                  onBlur={(e) => {
                    const newTags = [...(hero?.tags || [])];
                    newTags[i] = e.target.textContent;
                    onUpdateHero('tags', newTags.filter(t => t.trim()));
                  }}
                >
                  {tag}
                </span>
                <button 
                  onClick={() => {
                    const newTags = (hero?.tags || []).filter((_, idx) => idx !== i);
                    onUpdateHero('tags', newTags);
                  }}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    cursor: 'pointer', 
                    padding: '0', 
                    display: 'flex',
                    color: tagColor.text,
                    opacity: 0.7
                  }}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
          <button
            onClick={() => {
              const newTags = [...(hero?.tags || []), 'new tag'];
              onUpdateHero('tags', newTags);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              background: 'transparent',
              color: cssVars.textMuted,
              padding: '0.35rem 0.75rem',
              borderRadius: '100px',
              fontSize: '0.75rem',
              fontWeight: '500',
              border: `1px dashed ${cssVars.border}`,
              cursor: 'pointer'
            }}
          >
            <Plus className="w-3 h-3" /> Add tag
          </button>
        </div>

        {/* App Buttons - Always show Pitch to Me + other enabled apps */}
        <div style={{ marginTop: '32px' }}>
          <div style={{
            width: '100%',
            maxWidth: '200px',
            height: '1px',
            background: 'linear-gradient(90deg, #d1d5db 0%, #e5e7eb 50%, transparent 100%)',
            marginBottom: '28px'
          }} />
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '12px',
            alignItems: 'center'
          }}>
            {/* Pitch to Me button - always visible, clickable to edit */}
            <button
              onClick={() => {
                console.log('[HeroSection] Pitch to Me clicked, calling onEditPitchApp');
                onEditPitchApp && onEditPitchApp(pitchApp);
              }}
              data-testid="pitch-to-me-button"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px 24px',
                background: isPitchEnabled ? 'rgba(34, 197, 94, 0.08)' : 'rgba(34, 197, 94, 0.04)',
                color: '#4a5568',
                border: isPitchEnabled ? '1px solid rgba(34, 197, 94, 0.25)' : '1px solid rgba(34, 197, 94, 0.15)',
                borderRadius: '100px',
                fontSize: '14px',
                fontWeight: '500',
                letterSpacing: '0.01em',
                cursor: 'pointer',
                transition: 'all 0.25s ease',
                fontFamily: 'inherit'
              }}
              onMouseOver={e => {
                e.currentTarget.style.background = isPitchEnabled ? 'rgba(34, 197, 94, 0.14)' : 'rgba(34, 197, 94, 0.08)';
                e.currentTarget.style.borderColor = isPitchEnabled ? 'rgba(34, 197, 94, 0.4)' : 'rgba(34, 197, 94, 0.25)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseOut={e => {
                e.currentTarget.style.background = isPitchEnabled ? 'rgba(34, 197, 94, 0.08)' : 'rgba(34, 197, 94, 0.04)';
                e.currentTarget.style.borderColor = isPitchEnabled ? 'rgba(34, 197, 94, 0.25)' : 'rgba(34, 197, 94, 0.15)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {/* Paper plane icon */}
              <svg 
                width="15" 
                height="15" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke={isPitchEnabled ? '#22c55e' : '#86efac'}
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <path d="M22 2L11 13" />
                <path d="M22 2L15 22L11 13L2 9L22 2Z" />
              </svg>
              <span style={{ color: isPitchEnabled ? '#4a5568' : '#9CA3AF' }}>
                {pitchApp?.button_text || 'Pitch to me'}
                {!isPitchEnabled && ' (off)'}
              </span>
            </button>
            
            {/* Other enabled apps */}
            {apps.filter(a => a.enabled && a.type !== 'pitch').map((app) => (
              <AppButton 
                key={app.id} 
                app={app} 
                slug={slug}
                isEditorMode={true}
                onEdit={onEditApp}
              />
            ))}
            
            {/* Add more apps button */}
            <button
              onClick={() => onOpenAppsPanel && onOpenAppsPanel()}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                border: '1.5px dashed #D1D5DB',
                background: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                color: '#9CA3AF'
              }}
              onMouseOver={e => {
                e.currentTarget.style.borderColor = '#6366F1';
                e.currentTarget.style.color = '#6366F1';
                e.currentTarget.style.background = '#EEF2FF';
              }}
              onMouseOut={e => {
                e.currentTarget.style.borderColor = '#D1D5DB';
                e.currentTarget.style.color = '#9CA3AF';
                e.currentTarget.style.background = 'transparent';
              }}
              title="Add more apps"
            >
              <Plus style={{ width: '18px', height: '18px' }} />
            </button>
          </div>
        </div>
      </div>

      {/* Photo Section */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', alignSelf: 'flex-start', marginTop: '85px' }}>
        <input
          type="file"
          ref={photoInputRef}
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <div 
          onClick={() => !uploadingPhoto && photoInputRef.current?.click()}
          style={{
            width: 'clamp(180px, 20vw, 220px)',
            aspectRatio: '1/1',
            background: cssVars.bg,
            borderRadius: '50%',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 'clamp(50px, 8vw, 70px)',
            cursor: uploadingPhoto ? 'wait' : 'pointer',
            transition: 'opacity 0.2s',
            position: 'relative'
          }}
          className="hover:opacity-80"
        >
          {uploadingPhoto ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <Loader2 className="animate-spin" style={{ width: '32px', height: '32px', color: '#666' }} />
              <span style={{ fontSize: '12px', color: '#666' }}>Uploading...</span>
            </div>
          ) : hero?.photo_url ? (
            <img src={hero.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <span>👤</span>
              <span style={{ fontSize: '12px', color: '#999' }}>Click to upload</span>
            </div>
          )}
        </div>
        
        {/* Social links under photo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
          {connect?.linkedin_url && (
            <a href={connect.linkedin_url} target="_blank" rel="noopener noreferrer" 
              style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', transition: 'background 0.2s' }}
              className="hover:bg-gray-200"
            >
              <svg viewBox="0 0 24 24" style={{ width: '14px', height: '14px', fill: '#666' }}>
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
          )}
          {connect?.twitter_url && (
            <a href={connect.twitter_url} target="_blank" rel="noopener noreferrer"
              style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', transition: 'background 0.2s' }}
              className="hover:bg-gray-200"
            >
              <svg viewBox="0 0 24 24" style={{ width: '13px', height: '13px', fill: '#666' }}>
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
          )}
          {connect?.github_url && (
            <a href={connect.github_url} target="_blank" rel="noopener noreferrer"
              style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', transition: 'background 0.2s' }}
              className="hover:bg-gray-200"
            >
              <svg viewBox="0 0 24 24" style={{ width: '14px', height: '14px', fill: '#666' }}>
                <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
              </svg>
            </a>
          )}
          {connect?.instagram_url && (
            <a href={connect.instagram_url} target="_blank" rel="noopener noreferrer"
              style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', transition: 'background 0.2s' }}
              className="hover:bg-gray-200"
            >
              <svg viewBox="0 0 24 24" style={{ width: '14px', height: '14px', fill: '#666' }}>
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
            </a>
          )}
          <button 
            onClick={onEditSocials}
            style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer', transition: 'background 0.2s' }}
            className="hover:bg-gray-200"
            title="Edit social links"
          >
            <Plus className="w-4 h-4" style={{ color: '#999' }} />
          </button>
        </div>
      </div>
    </div>
  );
}
